import {useState, useCallback} from "react";
import apiClient from "../utils/apiClient";
import {useAuthPermissions} from "./useAuthPermissions";

const METHOD_SCOPE_MAP = {
  get: "read",
  post: "write",
  put: "write",
  patch: "write",
  delete: "delete"
};

const useApiRequest = ({baseUrl = "", resource = "", field = ""}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [response, setResponse] = useState(null);

  const {verifyScope} = useAuthPermissions();

  const makeRequest = useCallback(
    async (method, url, data = null, config = {}) => {
      setLoading(true);
      setError(null);

      try {
        // 🔐 Verificación de scopes
        const action = METHOD_SCOPE_MAP[method.toLowerCase()];
        const fieldScope = field ? `:${field}` : "";
        const requiredScope = resource && action ? `auth:${resource}:all${fieldScope}:${action}` : null;

        if (requiredScope && !verifyScope(requiredScope)) {
          const forbiddenError = new Error("Forbidden: Scope required - " + requiredScope);
          forbiddenError.code = "FORBIDDEN";
          setError(forbiddenError);

          return {
            success: false,
            error: forbiddenError,
            data: null
          };
        }

        // 🌐 URL completa
        const fullUrl = baseUrl ? `${baseUrl}${url}` : url;

        let axiosResponse;

        switch (method.toLowerCase()) {
          case "get":
            axiosResponse = await apiClient.get(fullUrl, config);
            break;
          case "post":
            axiosResponse = await apiClient.post(fullUrl, data, config);
            break;
          case "put":
            axiosResponse = await apiClient.put(fullUrl, data, config);
            break;
          case "patch":
            axiosResponse = await apiClient.patch(fullUrl, data, config);
            break;
          case "delete":
            axiosResponse = await apiClient.delete(fullUrl, config);
            break;
          default:
            throw new Error(`Método HTTP no soportado: ${method}`);
        }

        const enhancedResponse = {
          ...axiosResponse,
          method: method.toLowerCase(),
          url: fullUrl,
          timestamp: new Date().toISOString()
        };

        setData(axiosResponse.data);
        setResponse(enhancedResponse);

        return {
          success: true,
          data: axiosResponse.data,
          response: enhancedResponse
        };
      } catch (error) {
        const finalUrl = baseUrl ? `${baseUrl}${url}` : url;
        console.error(`Error en ${method.toUpperCase()} ${finalUrl}:`, error);
        setError(error);
        return {
          success: false,
          error: error,
          data: null,
          method: method.toLowerCase()
        };
      } finally {
        setLoading(false);
      }
    },
    [baseUrl, resource, field, verifyScope]
  );

  // CRUD helpers
  const create = useCallback((data, config) => makeRequest("post", "", data, config), [makeRequest]);

  const read = useCallback((id = "", config) => makeRequest("get", id ? `/${id}` : "", null, config), [makeRequest]);

  const update = useCallback((id, data, config) => makeRequest("put", `/${id}`, data, config), [makeRequest]);

  const remove = useCallback((id, config) => makeRequest("delete", `/${id}`, null, config), [makeRequest]);

  const list = useCallback((params, config) => makeRequest("get", "", null, {...config, params}), [makeRequest]);

  const clearError = useCallback(() => setError(null), []);
  const resetData = useCallback(() => setData(null), []);

  return {
    loading,
    error,
    data,
    response,

    makeRequest,
    create,
    read,
    update,
    remove,
    list,

    clearError,
    resetData,
    setData
  };
};

export default useApiRequest;
